import { Result } from "../../models/result.js";
import { LessonPlanReport } from "../../models/lesson_plan_report.js";
import { agentEndpoints } from "../apiConfig.js";
import httpRepo from "../httpRepo.js";

/**
 * Fetches Lesson Plan Performance Report for a given class and subject.
 * 
 * @param {Object} params
 * @param {string} params.schoolId - School ID (mandatory)
 * @param {number|string} params.classId - Class ID (mandatory)
 * @param {string} [params.subject] - Subject name
 * @param {number|string} [params.subjectId] - Subject ID
 * @param {number|string} [params.teacherId] - Optional Teacher ID
 * @param {string} [params.mainUserId] - Optional Main User ID
 * @returns {Promise<Result>} Result containing LessonPlanReport instance
 */
export const getLessonPlanPerformanceReport = async (params = {}) => {
    try {
        const { schoolId, classId, subject, subjectId, teacherId, mainUserId } = params;

        if (!schoolId) {
            return new Result({ message: "schoolId is required to fetch lesson plan report" });
        }
        if (!classId) {
            return new Result({ message: "classId is required to fetch lesson plan report" });
        }
        if (!subject && !subjectId) {
            return new Result({ message: "subject or subjectId is required to fetch lesson plan report" });
        }

        const queryParams = new URLSearchParams();
        queryParams.append('schoolId', schoolId);
        queryParams.append('classId', classId);
        if (subject) queryParams.append('subject', subject);
        if (subjectId) queryParams.append('subjectId', subjectId);
        if (teacherId) queryParams.append('teacherId', teacherId);
        if (mainUserId) queryParams.append('mainUserId', mainUserId);

        const url = `${agentEndpoints.getLessonPlanReport}?${queryParams.toString()}`;
        
        const response = await httpRepo.get(url);

        if (response && response.success && response.value) {
            const reportModel = LessonPlanReport.fromJson(response.value);
            return new Result({
                value: reportModel
            });
        }

        if (response && response.message) {
            return new Result({
                message: response.message,
                code: response.code
            });
        }

        return new Result({
            message: "Failed to retrieve lesson plan report data"
        });
    } catch (ex) {
        console.error(`[lessonPlanRepo] Error fetching lesson plan report:`, ex);
        return new Result({
            message: `Error fetching lesson plan report: ${ex.message || ex.toString()}`
        });
    }
};
